// The Alarms screen's order, filters and the navigation's count (alarms-and-scenes WEB-ALM-01 to -03). Pure, so it is
// tested without drawing.
import type { components } from "../../api/generated/vision-api";
import type { Space } from "../devices/queries";

export type Alarm = components["schemas"]["AlarmItem"];
export type Severity = Alarm["severity"];

/** Most serious first, as the screen and the filter list them. */
export const SEVERITIES = ["critical", "warning", "info"] as const satisfies readonly Severity[];
const RANK: Record<Severity, number> = { critical: 0, warning: 1, info: 2 };

/** Active before acknowledged; within each, critical, then warning, then info; newest first within a severity. */
export function sortAlarms(items: readonly Alarm[]): Alarm[] {
  return [...items].sort(
    (a, b) =>
      Number(a.acknowledged) - Number(b.acknowledged) ||
      RANK[a.severity] - RANK[b.severity] ||
      Date.parse(b.raised_at) - Date.parse(a.raised_at),
  );
}

/** The navigation's badge: alarms not yet acknowledged, and whether one of them is critical. */
export function alarmBadge(items: readonly Alarm[]): { count: number; critical: boolean } {
  const open = items.filter((a) => !a.acknowledged);
  return { count: open.length, critical: open.some((a) => a.severity === "critical") };
}

export interface AlarmFilters {
  severity?: Severity;
  space?: string;
  /** Only alarms not acknowledged yet. */
  open?: true;
  /** The Past 30 days tab (alarm-history AH-03). */
  past?: true;
  /** The Custom alarms tab (alarms-tab-custom-alarms CA-09); wins over `past`. */
  custom?: true;
}

/**
 * How an ended alarm ended, as the key of its words (AH-03): a silent device that was heard again says "Device back
 * online", one a person cleared names them.
 */
export function endedHow(a: Alarm): string {
  if (a.cleared_reason === "person") return a.cleared_by_name ? "alarms.how.personNamed" : "alarms.how.person";
  if (a.cleared_reason === "condition_ended" && a.source === "silence") return "alarms.how.backOnline";
  return `alarms.how.${a.cleared_reason ?? "condition_ended"}`;
}

/** The filters from the URL; anything else in it is dropped. */
export function parseAlarmFilters(s: Record<string, unknown>): AlarmFilters {
  return {
    ...(typeof s.severity === "string" && (SEVERITIES as readonly string[]).includes(s.severity)
      ? { severity: s.severity as Severity }
      : {}),
    ...(typeof s.space === "string" && /^[0-9a-f-]{36}$/i.test(s.space) ? { space: s.space } : {}),
    ...(s.open === true || s.open === "true" ? { open: true as const } : {}),
    ...(s.past === true || s.past === "true" ? { past: true as const } : {}),
    ...(s.custom === true || s.custom === "true" ? { custom: true as const } : {}),
  };
}

/** A space and every space below it. */
export function spaceAndBelow(spaces: readonly Space[], id: string): Set<string> {
  const out = new Set([id]);
  for (let grew = true; grew;) {
    grew = false;
    for (const s of spaces) {
      if (s.parent_id && out.has(s.parent_id) && !out.has(s.id)) {
        out.add(s.id);
        grew = true;
      }
    }
  }
  return out;
}

/** The alarms the filters keep; `spaceOf` gives an alarm device's space (unknown until its record is read). */
export function filterAlarms(
  items: readonly Alarm[],
  filters: AlarmFilters,
  spaceOf: (device: string) => string | null | undefined,
  spaces: readonly Space[],
): Alarm[] {
  const within = filters.space ? spaceAndBelow(spaces, filters.space) : undefined;
  return items.filter((a) => {
    if (filters.severity && a.severity !== filters.severity) return false;
    if (filters.open && a.acknowledged) return false;
    if (within) {
      const s = spaceOf(a.device);
      if (!s || !within.has(s)) return false;
    }
    return true;
  });
}

/**
 * A list after one alarm was raised, acknowledged or cleared (WEB-ALM-05, live 0.3.0 `alarm`, or the answer of the
 * acknowledge and clear routes): a cleared alarm leaves the list; any other replaces its row, or joins the list when new.
 */
export function applyAlarm(items: readonly Alarm[], alarm: Alarm, cleared = !!alarm.cleared_at): Alarm[] {
  if (cleared) return items.filter((a) => a.id !== alarm.id);
  return items.some((a) => a.id === alarm.id) ? items.map((a) => (a.id === alarm.id ? alarm : a)) : [alarm, ...items];
}
